// Product-page guide finder.
//
// A short series of questions on the product page that points visitors to the
// blog guides (and the exact sections in them) that answer what they're unsure
// about. Each product family gets its own config; the first config whose
// `matches()` returns true is shown. Add a new family by adding a config.
//
// Scoring: every answer adds points to specific guide sections. Guides are
// ranked by their total, and each guide shows its highest-scoring sections.
// Section headings must match the "## " headings in src/lib/blog-posts.ts
// exactly — they become the #anchors on the blog post page.

import type { LucideIcon } from "lucide-react";
import {
  Cable,
  Circle,
  CookingPot,
  Lightbulb,
  MoveVertical,
  RectangleHorizontal,
  Ruler,
  Sofa,
  Sun,
  UtensilsCrossed,
} from "lucide-react";
import type { Locale } from "./i18n";

type Copy = Record<Locale, string>;

export type GuideSection = {
  /** Post slug in src/lib/blog-posts.ts */
  post: string;
  /** Exact "## " heading text per locale */
  heading: Copy;
};

export type GuideOption = {
  id: string;
  icon: LucideIcon;
  label: Copy;
  hint?: Copy;
  /** Show the icon this many times (e.g. 2 for "several lamps"). */
  iconCount?: number;
  /** Section key → points */
  scores: Record<string, number>;
};

export type GuideStep = {
  id: string;
  /** Question; text between *asterisks* is set in the terracotta italic. */
  question: Copy;
  options: GuideOption[];
  /** Only ask this step when it returns true for the answers so far. */
  when?: (answers: Record<string, string>) => boolean;
};

export type ProductGuideConfig = {
  id: string;
  matches: (product: { productType?: string | null; title: string }) => boolean;
  eyebrow: Copy;
  title: Copy;
  intro: Copy;
  sections: Record<string, GuideSection>;
  steps: GuideStep[];
};

export type GuideRecommendation = {
  post: string;
  score: number;
  sections: GuideSection[];
};

// ---------- Murano loftlamper ----------

const PENDEL = "hvad-er-en-pendel-lampe";
const SPISEBORD = "hvor-hoejt-skal-en-lampe-haenge-over-spisebordet";

const muranoLoftlamper: ProductGuideConfig = {
  id: "murano-loftlamper",
  matches: ({ productType, title }) =>
    /murano loftlamp/i.test(productType ?? "") ||
    (/murano/i.test(title) && /(loftlamp|pendel|pendant)/i.test(title)),
  eyebrow: { da: "Guide til din loftlampe", en: "Guide to your ceiling lamp" },
  title: { da: "Find den rette *guide*", en: "Find the right *guide*" },
  intro: {
    da: "Svar på et par hurtige spørgsmål, så finder vi de afsnit i vores guides, der passer til dit rum.",
    en: "Answer a few quick questions and we'll find the parts of our guides that fit your room.",
  },
  sections: {
    "p.where": {
      post: PENDEL,
      heading: { da: "Hvor bruger man en pendel?", en: "Where do you use a pendant?" },
    },
    "p.vsCeiling": {
      post: PENDEL,
      heading: {
        da: "Pendel og loftlampe er ikke helt det samme",
        en: "A pendant and a ceiling lamp are not quite the same",
      },
    },
    "p.shape": {
      post: PENDEL,
      heading: { da: "Formen bestemmer meget af lyset", en: "The shape decides much of the light" },
    },
    "p.size": {
      post: PENDEL,
      heading: { da: "En stor eller lille pendel?", en: "A large or a small pendant?" },
    },
    "p.height": {
      post: PENDEL,
      heading: { da: "Så højt skal en pendel hænge", en: "How high a pendant should hang" },
    },
    "p.glass": {
      post: PENDEL,
      heading: {
        da: "Pendler i glas giver et andet udtryk",
        en: "Glass pendants give a different look",
      },
    },
    "p.several": {
      post: PENDEL,
      heading: { da: "Én eller flere pendler?", en: "One pendant or several?" },
    },
    "p.cord": {
      post: PENDEL,
      heading: {
        da: "Tænk over lampen, før du korter ledningen",
        en: "Think about the lamp before you shorten the cord",
      },
    },
    "s.height": {
      post: SPISEBORD,
      heading: { da: "Sådan finder du den rigtige højde", en: "How to find the right height" },
    },
    "s.sizeHeight": {
      post: SPISEBORD,
      heading: {
        da: "Lampens størrelse har betydning for højden",
        en: "The size of the lamp affects the height",
      },
    },
    "s.howFar": {
      post: SPISEBORD,
      heading: {
        da: "Hvor langt skal en lampe hænge over spisebordet?",
        en: "How far should a lamp hang above the dining table?",
      },
    },
    "s.two": {
      post: SPISEBORD,
      heading: {
        da: "Hvor højt skal to lamper hænge over spisebordet?",
        en: "How high should two lamps hang above the dining table?",
      },
    },
    "s.round": {
      post: SPISEBORD,
      heading: {
        da: "Den rette lampe til et rundt spisebord",
        en: "The right lamp for a round dining table",
      },
    },
    "s.size": {
      post: SPISEBORD,
      heading: {
        da: "Hvor stor skal lampen over spisebordet være?",
        en: "How big should the lamp above the dining table be?",
      },
    },
    "s.view": {
      post: SPISEBORD,
      heading: {
        da: "Tænk på udsynet omkring bordet",
        en: "Think about the view around the table",
      },
    },
    "s.light": {
      post: SPISEBORD,
      heading: {
        da: "Hvilket lys fungerer bedst over spisebordet?",
        en: "Which light works best above the dining table?",
      },
    },
    "s.mount": {
      post: SPISEBORD,
      heading: {
        da: "Find den rigtige højde, før lampen monteres færdigt",
        en: "Find the right height before the lamp is fully mounted",
      },
    },
  },
  steps: [
    {
      id: "room",
      question: { da: "Hvor skal lampen *hænge*?", en: "Where will the lamp *hang*?" },
      options: [
        {
          id: "dining",
          icon: UtensilsCrossed,
          label: { da: "Over spisebordet", en: "Above the dining table" },
          scores: { "s.howFar": 3, "s.view": 2, "s.height": 1 },
        },
        {
          id: "kitchen",
          icon: CookingPot,
          label: { da: "Over køkkenbordet eller -øen", en: "Above the kitchen counter or island" },
          scores: { "p.where": 3, "p.height": 2 },
        },
        {
          id: "room",
          icon: Sofa,
          label: { da: "I stue, soveværelse eller entré", en: "In a living room, bedroom or hall" },
          scores: { "p.where": 3, "p.vsCeiling": 2 },
        },
      ],
    },
    {
      id: "count",
      question: { da: "Én lampe eller *flere*?", en: "One lamp or *several*?" },
      options: [
        {
          id: "one",
          icon: Lightbulb,
          label: { da: "Én lampe", en: "One lamp" },
          hint: { da: "Lampen er hovedpersonen", en: "The lamp takes centre stage" },
          scores: { "p.size": 1, "s.size": 1 },
        },
        {
          id: "several",
          icon: Lightbulb,
          iconCount: 2,
          label: { da: "To eller flere i række", en: "Two or more in a row" },
          hint: { da: "Fx over et langt bord", en: "E.g. above a long table" },
          scores: { "p.several": 3, "s.two": 2 },
        },
      ],
    },
    {
      id: "table",
      when: (a) => a.room === "dining",
      question: { da: "Hvilken *form* har bordet?", en: "What *shape* is the table?" },
      options: [
        {
          id: "long",
          icon: RectangleHorizontal,
          label: { da: "Aflangt", en: "Rectangular" },
          scores: { "s.two": 2, "s.size": 1 },
        },
        {
          id: "round",
          icon: Circle,
          label: { da: "Rundt eller kvadratisk", en: "Round or square" },
          scores: { "s.round": 4 },
        },
      ],
    },
    {
      id: "doubt",
      question: { da: "Hvad er du mest i *tvivl* om?", en: "What are you most *unsure* about?" },
      options: [
        {
          id: "height",
          icon: MoveVertical,
          label: { da: "Hvor højt den skal hænge", en: "How high it should hang" },
          scores: { "s.height": 3, "s.howFar": 2, "p.height": 3, "s.mount": 1 },
        },
        {
          id: "size",
          icon: Ruler,
          label: { da: "Hvor stor den skal være", en: "How big it should be" },
          scores: { "s.size": 3, "s.sizeHeight": 2, "p.size": 3 },
        },
        {
          id: "light",
          icon: Sun,
          label: { da: "Hvordan lyset bliver", en: "What the light will be like" },
          scores: { "s.light": 3, "p.shape": 3, "p.glass": 2 },
        },
        {
          id: "cord",
          icon: Cable,
          label: { da: "Ledning og ophæng", en: "Cord and hanging" },
          scores: { "p.cord": 3, "s.mount": 3 },
        },
      ],
    },
  ],
};

export const PRODUCT_GUIDES: ProductGuideConfig[] = [muranoLoftlamper];

export function findProductGuide(product: {
  productType?: string | null;
  title: string;
}): ProductGuideConfig | undefined {
  return PRODUCT_GUIDES.find((g) => g.matches(product));
}

/** Steps that apply given the answers so far. */
export function activeSteps(
  config: ProductGuideConfig,
  answers: Record<string, string>,
): GuideStep[] {
  return config.steps.filter((s) => !s.when || s.when(answers));
}

const MAX_SECTIONS = 3;

export function recommendGuides(
  config: ProductGuideConfig,
  answers: Record<string, string>,
): GuideRecommendation[] {
  const sectionScores = new Map<string, number>();
  for (const step of activeSteps(config, answers)) {
    const option = step.options.find((o) => o.id === answers[step.id]);
    if (!option) continue;
    for (const [key, pts] of Object.entries(option.scores)) {
      sectionScores.set(key, (sectionScores.get(key) ?? 0) + pts);
    }
  }

  const byPost = new Map<string, { score: number; sections: [GuideSection, number][] }>();
  for (const [key, pts] of sectionScores) {
    const section = config.sections[key];
    if (!section) continue;
    const entry = byPost.get(section.post) ?? { score: 0, sections: [] };
    entry.score += pts;
    entry.sections.push([section, pts]);
    byPost.set(section.post, entry);
  }

  return [...byPost.entries()]
    .map(([post, e]) => ({
      post,
      score: e.score,
      sections: e.sections
        .sort((a, b) => b[1] - a[1])
        .slice(0, MAX_SECTIONS)
        .map(([s]) => s),
    }))
    .sort((a, b) => b.score - a.score);
}
